import React, { useCallback, useContext, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import AuthContext from 'providers/Auth/Auth.context';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import confirm from 'modules/confirm';
import api from 'api';
import { SUCCESS_MESSAGES } from 'request-responses';
import { COLOR_VARIANT } from 'models/Colors';
import TrashIcon from 'icons/Trash.icon';
import ActionItem from 'components/ActionItem';
import MailIcon from 'icons/Mail.icon';

import './AccountActions.styles.scss';

type AccountActionsProps = {
  className?: string;
};

const AccountActions: React.FC<AccountActionsProps> = (props) => {
  const { className } = props;

  const [isDeleting, setIsDeleting] = useState(false);
  const [isSubscribing, setIsSubscribing] = useState(false);

  const { popServerError, popSuccess } = useContext(PopNotificationsContext);
  const { logoutRequest } = useContext(AuthContext);
  const {
    currentUser: { isSubscribedToNewsletter },
    setCurrentUser,
  } = useContext(CurrentUserContext);

  const classes = classNames('account-actions', className);

  const { t } = useTranslation();

  const onDeleteAccount = useCallback(async () => {
    if (
      await confirm(
        {
          title: t('requestAccountDeletion'),
          content: t('requestAccountDeletionWarning'),
        },
        true,
      )
    ) {
      try {
        setIsDeleting(true);
        await api.users.deleteAccount();
        logoutRequest();
        popSuccess(t(SUCCESS_MESSAGES.DELETED_ACCOUNT));
      } catch (e) {
        popServerError(e);
      } finally {
        setIsDeleting(false);
      }
    }
  }, [logoutRequest, popServerError, popSuccess, t]);

  const onSubscribe = useCallback(async () => {
    try {
      setIsSubscribing(true);
      await api.users[
        isSubscribedToNewsletter
          ? 'unsubscribeFromNewsletter'
          : 'subscribeToNewsletter'
      ]();
      setCurrentUser((oldUser) => ({
        ...oldUser,
        isSubscribedToNewsletter: !isSubscribedToNewsletter,
      }));
      popSuccess(
        t(
          SUCCESS_MESSAGES[
            isSubscribedToNewsletter
              ? 'UNSUBSCRIBED_FROM_NEWSLETTER'
              : 'SUBSCRIBED_TO_NEWSLETTER'
          ],
        ),
      );
    } catch (e) {
      popServerError(e);
    } finally {
      setIsSubscribing(false);
    }
  }, [isSubscribedToNewsletter, popServerError, popSuccess, setCurrentUser, t]);

  const items = useMemo(
    () => [
      {
        title: t(isSubscribedToNewsletter ? 'unsubscribe' : 'subscribe'),
        description: t('emailPreferecnces'),
        color: COLOR_VARIANT[isSubscribedToNewsletter ? 'ORANGE' : 'BLUE'],
        showLoader: isSubscribing,
        disabled: isSubscribing,
        Icon: MailIcon,
        onClick: onSubscribe,
      },
      {
        title: t('deleteAccount'),
        description: t('removeYourData'),
        color: COLOR_VARIANT.RED,
        showLoader: isDeleting,
        disabled: isDeleting,
        Icon: TrashIcon,
        onClick: onDeleteAccount,
      },
    ],
    [
      isDeleting,
      isSubscribedToNewsletter,
      isSubscribing,
      onDeleteAccount,
      onSubscribe,
      t,
    ],
  );

  return (
    <div className={classes}>
      {items.map((item) => (
        <ActionItem key={item.title} {...item} />
      ))}
    </div>
  );
};

export default AccountActions;
